import { createContext } from 'react'
import type { User, AuthCredential } from 'firebase/auth'
import type { UserAccountData } from '@/firebase/auth/user-account-data'
import { kDefaultLogoPath } from '@/constants/constants-ui'
import { EMPTY_USER_IDENTITY, type UserIdentity, type UserProfileSnapshot } from '@/account/utils/resolve-user-identity'

// AuthContext type
type AuthContextType = {
  user: User | null
  updated: string | null
  loading: boolean
  logoUrl: string
  // Optional so the many inline AuthContext mocks across tests/stories don't all need updating;
  // the real provider always supplies it. Resolves `false` when a transient fetch failure left the
  // current logo in place (so the caller can toast) and `true` when the logo was refreshed.
  refreshOrgLogo?: () => Promise<boolean>
  linkCredential: (credential: AuthCredential) => Promise<void>
  userAccountData: UserAccountData | null
  refreshUserAccountData: () => Promise<UserAccountData | null>
  clearAuthStateGlobalAndContext: (props: { loading: boolean }) => void
  pollingStarted: boolean
  // Resolved identity for navigation surfaces. Derived in the provider from the canonical profile
  // snapshot (with a transitional Firebase fallback) so consumers never recombine name fields.
  // Optional (like `refreshOrgLogo`) so the many inline AuthContext mocks across tests/stories don't
  // all need updating; the real provider always supplies it, and consumers fall back to EMPTY_USER_IDENTITY.
  userIdentity?: UserIdentity
  // Single integration point for the future profile editor (FE-772): replace the snapshot and the
  // derived identity republishes. Replacing with a new object guarantees a context update.
  setUserProfile?: (profile: UserProfileSnapshot) => void
}

// Default context values
const defaultAuthContextValues: AuthContextType = {
  user: null,
  updated: null,
  loading: true,
  logoUrl: kDefaultLogoPath,
  refreshOrgLogo: async () => {
    return true
  },
  linkCredential: async () => {
    return
  },
  userAccountData: null,
  refreshUserAccountData: async () => null,
  clearAuthStateGlobalAndContext: () => {
    return
  },
  pollingStarted: false,
  userIdentity: EMPTY_USER_IDENTITY,
  setUserProfile: () => {
    return
  },
}

export const AuthContext = createContext<AuthContextType>(defaultAuthContextValues)
